import React from 'react';
import {
    LineChart, Line, BarChart, Bar,
    XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell, Area, AreaChart
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Timeline Chart Component
export const BookingsTimelineChart = ({ data }) => {
    const hasData = data && data.length > 0;

    return (
        <div className="bg-white rounded-xl shadow-sm p-6 h-full border border-gray-200 hover:shadow-md transition-shadow flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Bookings Timeline</h3>
            <div className="flex-1 min-h-0">
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.1} />
                                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                            <XAxis
                                dataKey="date"
                                stroke="#9CA3AF"
                                tick={{ fontSize: 11, fill: '#6B7280' }}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                            />
                            <YAxis
                                stroke="#9CA3AF"
                                tick={{ fontSize: 11, fill: '#6B7280' }}
                                tickLine={false}
                                axisLine={false}
                                dx={-10}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    padding: '12px'
                                }}
                                itemStyle={{ fontSize: '12px', fontWeight: 500 }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }} />
                            <Area
                                type="monotone"
                                dataKey="count"
                                stroke="#3B82F6"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorBookings)"
                                name="Bookings"
                                activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="revenue"
                                stroke="#10B981"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorRevenue)"
                                name="Revenue (₹)"
                                activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                        <div className="text-center">
                            <p className="text-base font-medium">No data available</p>
                            <p className="text-xs mt-1">Bookings timeline will appear here</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Bookings by Event Bar Chart
export const BookingsByEventChart = ({ data }) => {
    const hasData = data && data.length > 0;

    return (
        <div className="bg-white rounded-xl shadow-sm p-6 h-full border border-gray-200 hover:shadow-md transition-shadow flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Bookings by Event</h3>
            <div className="flex-1 min-h-0">
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                            <XAxis
                                dataKey="eventName"
                                angle={-15}
                                textAnchor="end"
                                height={60}
                                stroke="#9CA3AF"
                                tick={{ fontSize: 10, fill: '#6B7280' }}
                                tickLine={false}
                                axisLine={false}
                                interval={0}
                            />
                            <YAxis
                                stroke="#9CA3AF"
                                tick={{ fontSize: 11, fill: '#6B7280' }}
                                tickLine={false}
                                axisLine={false}
                                dx={-10}
                            />
                            <Tooltip
                                cursor={{ fill: '#F9FAFB' }}
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    padding: '12px'
                                }}
                                itemStyle={{ fontSize: '12px', fontWeight: 500 }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '10px', fontSize: '12px' }} />
                            <Bar
                                dataKey="count"
                                fill="#3B82F6"
                                name="Bookings"
                                radius={[4, 4, 0, 0]}
                                barSize={30}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                        <div className="text-center">
                            <p className="text-base font-medium">No data available</p>
                            <p className="text-xs mt-1">Event breakdown will appear here</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Payment Methods Pie Chart
export const PaymentMethodsChart = ({ data }) => {
    const hasData = data && data.length > 0;

    return (
        <div className="bg-white rounded-xl shadow-sm p-6 h-full border border-gray-200 hover:shadow-md transition-shadow flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Payment Methods</h3>
            <div className="flex-1 min-h-0">
                {hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={80}
                                paddingAngle={5}
                                dataKey="count"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                    padding: '12px'
                                }}
                                itemStyle={{ fontSize: '12px', fontWeight: 500 }}
                            />
                            <Legend
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-400">
                        <div className="text-center">
                            <p className="text-base font-medium">No data available</p>
                            <p className="text-xs mt-1">Payment methods will appear here</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default { BookingsTimelineChart, BookingsByEventChart, PaymentMethodsChart };
